"use client";

import { ToggleButton, Tooltip, Theme, useMediaQuery } from "@mui/material";
import * as Icons from "@mui/icons-material";
import { useState, useEffect, useCallback } from "react";
import { useThemeContext } from "@/components/provider/ThemeProvider";
import { darkTheme, lightTheme } from "@/styles/theme";
import { useD3ClientPage } from "@/hook/useD3ClientPage";

const themes: Record<string, Theme> = {
  light: lightTheme,
  dark: darkTheme,
};

type ThemeMode = "light" | "dark";
const themeOrder: ThemeMode[] = ["light", "dark"];

export default function ThemeToggle() {
  const { t } = useD3ClientPage();
  const { setTheme } = useThemeContext();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [selectedTheme, setSelectedTheme] = useState<ThemeMode>("light");

  // applyTheme을 useCallback으로 최적화
  const applyTheme = useCallback(
    (newTheme: ThemeMode) => {
      localStorage.setItem("theme", newTheme);
      document.documentElement.setAttribute("data-theme", newTheme);
      document.cookie = `theme=${newTheme}; path=/; max-age=31536000`;
      setTheme(themes[newTheme]);

      // 클라이언트에서 <meta name="theme-color">를 즉시 업데이트
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        const color =
          newTheme === "dark" ? darkTheme.palette.background.default : lightTheme.palette.background.default;
        metaThemeColor.setAttribute("content", color);
      }
    },
    [setTheme]
  ); // setTheme이 변경될 때만 함수가 새로 생성됨

  useEffect(() => {
    const storedTheme = typeof window !== "undefined" ? localStorage.getItem("theme") : null;
    const validThemes = ["light", "dark"];
    const initialTheme = validThemes.includes(storedTheme as any)
      ? (storedTheme as ThemeMode)
      : prefersDarkMode
      ? "dark"
      : "light";
    setSelectedTheme(initialTheme);
    applyTheme(initialTheme);
  }, [prefersDarkMode, applyTheme]); // applyTheme을 안전하게 의존성에 추가

  const handleToggleClick = () => {
    const currentIndex = themeOrder.indexOf(selectedTheme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    const nextTheme = themeOrder[nextIndex];
    setSelectedTheme(nextTheme);
    applyTheme(nextTheme);
  };

  const getIconAndTitle = () => {
    if (selectedTheme === "light") {
      return {
        icon: <Icons.DarkModeOutlined />, // light일 때 달 (dark로 전환 암시)
        title: t("tooltip.darkMode"), // "Dark mode"로 전환
      };
    } else {
      return {
        icon: <Icons.LightModeOutlined />, // dark일 때 태양 (light로 전환 암시)
        title: t("tooltip.lightMode"), // "Light mode"로 전환
      };
    }
  };

  const { icon, title } = getIconAndTitle();

  return (
    <Tooltip title={title} arrow>
      <ToggleButton
        value={selectedTheme}
        onClick={handleToggleClick}
        color="primary"
        sx={{ border: "none", borderRadius: "50%", padding: "6px" }}
      >
        {icon}
      </ToggleButton>
    </Tooltip>
  );
}
